import { useCallback, useMemo } from "react";

import { Localized, useLocalization } from "@fluent/react";
import {
  Button,
  Dialog,
  Select,
  TextInput,
  useBreakpoints,
} from "@narmi/design_system";
import { incomeReporting } from "byzantine";
import cc from "classcat";

import { formAdapters } from "../../..";
import { useIncomeReportingCard } from "../hooks";

import styles from "./IncomeReportingForm.module.scss";

import type { IncomeReportingDialogProps } from "./types";

const {
  useIncomeReportingForm,
  useIsIncomeReportingEnabled,
  INCOME_TYPE_TRANSLATION_KEYS,
  INCOME_FIELD_TRANSLATION_DEFAULTS,
  INCOME_REPORTING_INDUSTRY_CHOICES,
} = incomeReporting;

const noop = () => null;

export const IncomeReportingForm = ({
  uuid,
  onSuccess = noop,
  onUserDismiss = noop,
  ...dialogProps
}: IncomeReportingDialogProps) => {
  const { s } = useBreakpoints();
  const { l10n } = useLocalization();
  const { updateIncomeSources } = useIncomeReportingCard();
  const { fieldsToDisplay } = useIsIncomeReportingEnabled();
  const { form, submitIncomeReport } = useIncomeReportingForm();
  const { resetForm } = form;

  const cancelHandler = useCallback(() => {
    onUserDismiss();
    resetForm();
  }, [onUserDismiss, resetForm]);

  const submitHandler = useCallback(() => {
    submitIncomeReport(uuid, {
      onSuccess: (data) => {
        updateIncomeSources(data);
        onUserDismiss();
        onSuccess();
      },
    });
  }, [onUserDismiss, onSuccess, submitIncomeReport, updateIncomeSources, uuid]);

  const footerButtons = useMemo(
    () => (
      <div className={styles["button-container"]}>
        <Button
          label={l10n.getString("income-reporting-form-cancel", null, "Cancel")}
          kind="negative"
          onClick={cancelHandler}
        />
        <Button
          label={l10n.getString("income-reporting-form-save", null, "Save")}
          onClick={submitHandler}
        />
      </div>
    ),
    [cancelHandler, l10n, submitHandler]
  );

  return (
    <Dialog
      {...dialogProps}
      onUserDismiss={cancelHandler}
      title={l10n.getString(
        "income-reporting-form-title",
        null,
        "Add your income information"
      )}
      footer={footerButtons}
    >
      <div
        className={cc([styles["button-container"], styles["input-container"]])}
      >
        <TextInput
          inputMode="numeric"
          label={l10n.getString(
            "income-reporting-display-section-income",
            {},
            "Annual income"
          )}
          {...formAdapters.amountInput(form, "income")}
        />

        {fieldsToDisplay.includes("mortgage_rent") && (
          <TextInput
            inputMode="numeric"
            label={l10n.getString(
              "income-reporting-display-section-mortgage-rent",
              {},
              "Monthly mortgage/rent payments"
            )}
            {...formAdapters.amountInput(form, "mortgageRent")}
          />
        )}

        <Select
          label={l10n.getString(
            "income-reporting-display-section-income-type",
            {},
            "Type of employment"
          )}
          {...formAdapters.select(form, "incomeType")}
        >
          {(
            Object.entries(INCOME_TYPE_TRANSLATION_KEYS) as Array<
              [keyof typeof INCOME_FIELD_TRANSLATION_DEFAULTS, string]
            >
          ).map(([incomeType, translationKey]) => (
            <Select.Item key={incomeType} value={incomeType}>
              <Localized id={translationKey}>
                <span>{INCOME_FIELD_TRANSLATION_DEFAULTS[incomeType]}</span>
              </Localized>
            </Select.Item>
          ))}
        </Select>

        {fieldsToDisplay.includes("industry") && (
          <Select
            label={l10n.getString(
              "income-reporting-display-section-industry",
              {},
              "Industry"
            )}
            {...formAdapters.select(form, "industry")}
          >
            {Object.keys(INCOME_REPORTING_INDUSTRY_CHOICES).map((industry) => (
              <Select.Item key={industry} value={industry}>
                <span
                  style={{
                    display: "inline-block",
                    maxWidth: s ? "350px" : "250px",
                  }}
                >
                  {industry}
                </span>
              </Select.Item>
            ))}
          </Select>
        )}

        {fieldsToDisplay.includes("occupation") && (
          <TextInput
            label={l10n.getString(
              "income-reporting-display-section-occupation",
              {},
              "Occupation"
            )}
            maxLength={128}
            {...formAdapters.input(form, "occupation")}
          />
        )}

        <span className={styles["disclaimer-text"]}>
          <Localized id="income-reporting-form-disclaimer">
            The information you provide may be used to personalize financial
            product recommendations, including credit offers.
          </Localized>
        </span>
      </div>
    </Dialog>
  );
};
